import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Container, Row, Col } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import styles from './ArchaeologicalSection.module.css';

import hadarImage from '../../../Assets/Image/Attraction/historical/archiological/hadar3.jpg';
import gonaImage from '../../../Assets/Image/Attraction/historical/archiological/gona2.jpg';
import dikikaImage from '../../../Assets/Image/Attraction/historical/archiological/dikika2.jpg';
import middleAwashImage from '../../../Assets/Image/Attraction/historical/archiological/middleAwash.jpg';
import lediGeraruImage from '../../../Assets/Image/Attraction/historical/archiological/gona.jpg';
import bouriImage from '../../../Assets/Image/Attraction/historical/archiological/buri.png';
import melkaKuntureImage from '../../../Assets/Image/Attraction/historical/archiological/melkaKunture.jpg';
import banner from '../../../Assets/Image/Attraction/historical/archiological/gona.jpg';

// Archaeological sites data
const archaeologicalSites = [
  {
    id: 1,
    title: "Hadar",
    description: "Home to one of the oldest hominid fossils, known as 'Lucy'.",
    image: hadarImage,
    category: "Paleoanthropology"
  },
  {
    id: 2,
    title: "Gona",
    description: "Significant archaeological site with ancient stone tools.",
    image: gonaImage,
    category: "Paleoanthropology"
  },
  {
    id: 3,
    title: "Dikika",
    description: "Famous for the discovery of early human remains.",
    image: dikikaImage,
    category: "Paleoanthropology"
  },
  {
    id: 4,
    title: "Middle Awash",
    description: "Rich archaeological site with early hominid evidence.",
    image: middleAwashImage,
    category: "Paleoanthropology"
  },
  {
    id: 5,
    title: "Ledi-Geraru",
    description: "Location of important findings of early human fossils.",
    image: lediGeraruImage,
    category: "Paleoanthropology"
  },
  {
    id: 6,
    title: "Bouri",
    description: "Significant site for prehistoric remains.",
    image: bouriImage,
    category: "Paleoanthropology"
  },
  {
    id: 7,
    title: "Melka Kunture",
    description: "An archaeological site known for its rich hominid history.",
    image: melkaKuntureImage,
    category: "Paleoanthropology"
  }
];

const Archaeological = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { 
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const bannerVariants = {
    hidden: { scale: 1.2, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 1.2,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className={styles.archaeologicalSection}>
    <motion.div 
      className={styles.heroBanner}
      initial="hidden"
      animate="visible"
      variants={bannerVariants}
    >
      <div className={styles.bannerContent}>
        <motion.h1
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          Archaeological Wonders of Afar
        </motion.h1>
        <motion.p
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.8 }}
        >
          Discover the cradle of humanity and explore millions of years of history
        </motion.p>
      </div>
      <div className={styles.bannerOverlay}></div>
      <img src={banner} alt="Archaeological Banner" className={styles.bannerImage} />
    </motion.div>

    <Container className={styles.mainContent}>
      <motion.div
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={controls}
        className={styles.sitesContainer}
      >
        <Row className="g-4">
          {archaeologicalSites.map((site, index) => (
            <Col key={site.id} lg={4} md={6} sm={12}>
              <motion.div 
                className={styles.siteCard}
                variants={itemVariants}
                whileHover={{ 
                  y: -10,
                  transition: { duration: 0.3 }
                }}
              >
                <div className={styles.imageWrapper}>
                  <img src={site.image} alt={site.title} className={styles.cardImage} />
                  <div className={styles.imageOverlay}>
                    <motion.span
                      className={styles.categoryBadge}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.3 + index * 0.1 }}
                    >
                      {site.category}
                    </motion.span>
                  </div>
                </div>
                <div className={styles.cardContent}>
                  <h3>{site.title}</h3>
                  <p>{site.description}</p>
                  <div className={styles.cardFooter}>
                    <button className={styles.exploreBtn}>Explore More</button>
                  </div>
                </div>
              </motion.div>
            </Col>
          ))}
        </Row>
      </motion.div>
    </Container>
  </section>
  );
};

export default Archaeological;